import { TRADE_CATEGORIES } from '@/lib/trade-presets';

/**
 * The trade gate — who Mucka is open to right now.
 *
 * From 2026-08 signup is restricted to heating, plumbing, air-con and the
 * adjacent renewables trades. Everyone else still picks their trade (so we
 * capture the demand and can tell them when we open up), but no account,
 * no auth user and no confirmation email is created for them — they land on
 * the "coming soon" screen and their details go to `trade_waiting_list`.
 *
 * 🚨 The gate is enforced SERVER-SIDE in POST /api/auth/signup, not here.
 * This module is shared by the picker and the route so the two can't drift,
 * but a client-only gate is one `curl` away from being no gate at all.
 *
 * Eligibility is by CATEGORY, not by an enumerated list of trade slugs. A new
 * trade added to `Heating & Plumbing` in trade-presets.ts is automatically
 * allowed in, which is the behaviour we want: the categories are the product
 * decision, the slugs inside them are just the vocabulary.
 */
export const ELIGIBLE_TRADE_CATEGORIES = ['Heating & Plumbing', 'HVAC & Renewables'] as const;

const ELIGIBLE_SET = new Set<string>(ELIGIBLE_TRADE_CATEGORIES);

/** Every trade slug the gate lets through, built from the categories above. */
export const ELIGIBLE_TRADE_VALUES: string[] = Object.entries(TRADE_CATEGORIES)
  .filter(([category]) => ELIGIBLE_SET.has(category))
  .flatMap(([, trades]) => trades.map((t) => t.value));

const ELIGIBLE_VALUE_SET = new Set(ELIGIBLE_TRADE_VALUES);

/**
 * The picker's option groups: the categories we serve first, everything else
 * after in its original order. Someone eligible should find themselves at the
 * top of the list rather than scrolling past eight categories that will bounce
 * them.
 */
export const SIGNUP_TRADE_GROUPS: {
  category: string;
  eligible: boolean;
  trades: { value: string; label: string }[];
}[] = [
  ...Object.entries(TRADE_CATEGORIES)
    .filter(([category]) => ELIGIBLE_SET.has(category))
    // Sort by the declared order of ELIGIBLE_TRADE_CATEGORIES, not by however
    // TRADE_CATEGORIES happens to be keyed.
    .sort(
      ([a], [b]) =>
        ELIGIBLE_TRADE_CATEGORIES.indexOf(a as (typeof ELIGIBLE_TRADE_CATEGORIES)[number]) -
        ELIGIBLE_TRADE_CATEGORIES.indexOf(b as (typeof ELIGIBLE_TRADE_CATEGORIES)[number]),
    )
    .map(([category, trades]) => ({ category, eligible: true, trades })),
  ...Object.entries(TRADE_CATEGORIES)
    .filter(([category]) => !ELIGIBLE_SET.has(category))
    .map(([category, trades]) => ({ category, eligible: false, trades })),
];

/** Is this trade slug one we're currently open to? Unknown slug → false. */
export function isEligibleTrade(trade: string | null | undefined): boolean {
  return typeof trade === 'string' && ELIGIBLE_VALUE_SET.has(trade);
}

const LABELS = new Map(
  Object.values(TRADE_CATEGORIES)
    .flat()
    .map((t) => [t.value, t.label] as const),
);

/** Human label for a trade slug, or null if we don't recognise it. */
export function tradeLabel(trade: string | null | undefined): string | null {
  if (typeof trade !== 'string') return null;
  return LABELS.get(trade) ?? null;
}

/** Does this slug name a trade at all? Guards the waiting-list write. */
export function isKnownTrade(trade: string | null | undefined): boolean {
  return tradeLabel(trade) !== null;
}
